import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  ColumnDef,
  flexRender,
  OnChangeFn,
  Row,
  SortingState,
} from "@tanstack/react-table";
import { Fragment, ReactElement } from "react";
import Loading from "../Loading";

export type TableProps<TData> = {
  data?: TData[];
  isLoading?: boolean;
  columns: ColumnDef<TData>[];
  placeholder?: string;
  renderSubComponent?: (props: { row: Row<TData> }) => React.ReactElement;
  context?: Record<string, any>;
  enableSorting?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualSorting?: boolean;
};

function Table<T>({
  isLoading = false,
  data = [],
  columns,
  renderSubComponent,
  placeholder,
  Footer,
  context = {},
  enableSorting = false,
  sorting,
  onSortingChange,
  manualSorting = false,
}: TableProps<T> & { Footer?: ReactElement }) {
  const table = useReactTable<T>({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting,
    sortDescFirst: false,
    state: sorting === undefined ? undefined : { sorting },
    manualSorting,
    ...(onSortingChange ? { onSortingChange } : {}),
  });

  const rows = table.getRowModel().rows;

  return (
    <table className="w-full">
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const canSort = enableSorting && header.column.getCanSort();
              const sortDirection = header.column.getIsSorted();
              const ariaSort =
                sortDirection === "asc"
                  ? "ascending"
                  : sortDirection === "desc"
                  ? "descending"
                  : "none";
              const sortIndicator =
                sortDirection === "asc"
                  ? "↑"
                  : sortDirection === "desc"
                  ? "↓"
                  : "↕";

              return (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  aria-sort={canSort ? ariaSort : undefined}
                >
                  {header.isPlaceholder ? null : (
                    canSort ? (
                      <button
                        type="button"
                        onClick={header.column.getToggleSortingHandler()}
                      >
                        <span>
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                        </span>
                        <span aria-hidden="true">{sortIndicator}</span>
                      </button>
                    ) : (
                      <div>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </div>
                    )
                  )}
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody className="align-top">
        {isLoading && (
          <tr>
            <td colSpan={10} className="text-center text-lg">
              <Loading size={30} className="m-auto block loading" />
            </td>
          </tr>
        )}

        {!rows?.length && !isLoading ? (
          <tr>
            <td colSpan={12} className="text-center">
              {placeholder || "No competition"}
            </td>
          </tr>
        ) : (
          rows.map((row) => {
            return (
              <Fragment key={row.id}>
                {renderSubComponent?.({ row })}

                <tr>
                  {/* first row is a normal row */}
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, {
                          ...cell.getContext(),
                          ...context,
                        })}
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            );
          })
        )}
      </tbody>
      <tfoot>
        {Footer ||
          table.getFooterGroups().map((footerGroup) => (
            <tr key={footerGroup.id}>
              {footerGroup.headers.map((header) => (
                <th key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.footer,
                        header.getContext()
                      )}
                </th>
              ))}
            </tr>
          ))}
      </tfoot>
    </table>
  );
}

export default Table;
